<template>
  <div v-if="isDataLoaded">
    <!-- Company Summary Section -->
    <div>
      <div class="company-summary-header-container">
        <div class="company-summary-header-title">Company Summary</div>
      </div>
      <div class="company-summary-content">
        <div class="tile company-summary-container">
          <!-- Capital -->
          <div class="company-items-container">
            <div class="chart-main-title">Capital</div>
            <EditableCompanyItem 
              title="Debt" 
              :items="investorSponsorTypes.lenders" 
              :project-id="selectedId"
              @updated="handleInvestorUpdate"
            />
            <EditableCompanyItem 
              title="Equity" 
              :items="investorSponsorTypes.investors" 
              :project-id="selectedId"
              @updated="handleInvestorUpdate"
            />
          </div>
          <!-- Management -->
          <div class="company-items-container">
            <div class="chart-main-title">Management</div>
            <EditableCompanyItem 
              title="Developer(s)" 
              :items="investorSponsorTypes.developers" 
              :project-id="selectedId"
              @updated="handleInvestorUpdate"
            />
            <div class="company-items-content">
              <div class="company-title">General Contractor(s)</div>
              <div v-if="projectGC" class="company-list">
                <div class="company-item">
                  <router-link 
                    :to="`/companies/contractors/${projectGC.id}`" 
                    class="company-link"
                    @click.stop
                  >
                    {{ projectGC.name }}
                  </router-link>
                </div>
              </div>
              <div class="no-companies" v-else>
                N/A
              </div>
              <button @click="editGeneralContractor" class="edit-btn">✏️</button>
            </div>
            <EditableCompanyItem 
              title="Owner's Representative" 
              :items="consultantTypesData.owners_reps" 
              :project-id="selectedId"
              @updated="handleConsultantUpdate"
            />
          </div>
          <!-- Design -->
          <div class="company-items-container">
            <div class="chart-main-title">Design</div>
            <EditableCompanyItem 
              title="Design Architect" 
              :items="consultantTypesData.design_architects" 
              consultant-type="design_architect"
              :project-id="selectedId"
              @updated="handleConsultantUpdate"
            />
            <EditableCompanyItem 
              title="Architect of Record" 
              :items="consultantTypesData.architects" 
              consultant-type="architect"
              :project-id="selectedId"
              @updated="handleConsultantUpdate"
            />
            <EditableCompanyItem 
              title="Interior Design" 
              :items="consultantTypesData.interior_designers" 
              consultant-type="interior_design"
              :project-id="selectedId"
              @updated="handleConsultantUpdate"
            />
            <EditableCompanyItem 
              title="Landscape Architect" 
              :items="consultantTypesData.landscape_architects" 
              consultant-type="landscape_architect"
              :project-id="selectedId"
              @updated="handleConsultantUpdate"
            />
          </div>
          <!-- Engineering -->
          <div class="company-items-container">
            <div class="chart-main-title">Engineering</div>
            <div class="engineering-items-container">
              <div>
                <EditableCompanyItem 
                  title="Civil Engineer" 
                  :items="consultantTypesData.civil_engineers" 
                  consultant-type="civil_engineer"
                  :project-id="selectedId"
                  @updated="handleConsultantUpdate"
                />
                <EditableCompanyItem 
                  title="Structural Engineer" 
                  :items="consultantTypesData.structural_engineers" 
                  consultant-type="structural_engineer"
                  :project-id="selectedId"
                  @updated="handleConsultantUpdate"
                />
                <EditableCompanyItem 
                  title="Mechanical" 
                  :items="consultantTypesData.mechanical_engineers" 
                  consultant-type="mep_engineer"
                  :project-id="selectedId"
                  @updated="handleConsultantUpdate"
                />
                <EditableCompanyItem 
                  title="Electrical" 
                  :items="consultantTypesData.electrical_engineers" 
                  consultant-type="electrical_engineer"
                  :project-id="selectedId"
                  @updated="handleConsultantUpdate"
                />
              </div>
              <div>
                <EditableCompanyItem 
                  title="Plumbing" 
                  :items="consultantTypesData.plumbing_engineers" 
                  consultant-type="plumbing_engineer"
                  :project-id="selectedId"
                  @updated="handleConsultantUpdate"
                />
                <EditableCompanyItem 
                  title="Fire Protection" 
                  :items="consultantTypesData.fire_protection_engineers" 
                  consultant-type="fire_protection_engineer"
                  :project-id="selectedId"
                  @updated="handleConsultantUpdate"
                />
                <EditableCompanyItem 
                  title="Environmental" 
                  :items="consultantTypesData.environmental_engineers" 
                  consultant-type="environmental_engineer"
                  :project-id="selectedId"
                  @updated="handleConsultantUpdate"
                />
                <EditableCompanyItem 
                  title="Geotechnical Engineer" 
                  :items="consultantTypesData.geotechnical_engineers" 
                  consultant-type="geotechnical_engineer"
                  :project-id="selectedId"
                  @updated="handleConsultantUpdate"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contractor Performance Section -->
    <div>
      <div class="contractor-performance-header-container">
        <div class="contractor-performance-header-title">Contractor Performance</div>
        <div class="contractor-performance-subtitle">Performance data from G703 documents</div>
      </div>
      <div class="grid-container" id="contractorPerformanceGrid">
        <ag-grid
          :row-height="35"
          :row-data="contractorPerformanceData"
          :columnDefs="contractorPerformanceColumnDefs"
          :treeData="false"
          :groupDefaultExpanded="0"
          :key="`contractor_performance_${contractorPerformanceDataCtr}`"
          @grid-ready="onContractorPerformanceGridReady"
        />
      </div>
    </div>

    <!-- GC Edit Modal -->
    <div v-if="gcEditModal.isOpen" class="modal-overlay" @click="closeGCEditModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Edit General Contractor</h3>
          <button class="close-btn" @click="closeGCEditModal">×</button>
        </div>
        <div class="modal-body">
          <div class="search-container">
                          <input
                v-model="gcEditModal.searchTerm"
                @input="searchContractors"
                placeholder="Search contractors..."
                class="search-input"
                autofocus
              />
            <div v-if="gcEditModal.showDropdown && gcEditModal.searchResults.length > 0" class="search-dropdown">
              <div
                v-for="contractor in gcEditModal.searchResults"
                :key="contractor.id || contractor.name"
                class="search-result-item"
                @click="selectContractor(contractor)"
              >
                <div class="contractor-name">{{ contractor.name }}</div>
                <div class="contractor-type">{{ contractor.type || 'registered' }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeGCEditModal" class="cancel-btn">Cancel</button>
          <button @click="saveGeneralContractor" class="save-btn" :disabled="!gcEditModal.selectedContractor">
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="no-data-loaded">
    An error occurred. No data has been fetched.
  </div>

  <!-- DDA Term Edit Modal (copied from ProjectSummary) -->
  <div v-if="ddaTermModalData.isOpen" class="modal-backdrop" @click="closeDDATermModal">
    <div class="contractor-modal dda-term-modal" @click.stop>
      <div class="modal-header">
        <h3>Edit DDA Term</h3>
        <button class="close-button" @click="closeDDATermModal">×</button>
      </div>
      
      <div class="modal-body">
        <div class="search-section">
          <label>Search DDA Terms:</label>
          <input 
            type="text" 
            v-model="ddaTermModalData.searchTerm" 
            @input="filterDDATerms"
            placeholder="Type to search terms..."
            class="search-input"
          />
        </div>
        
        <div v-if="ddaTermModalData.searchResults.length > 0" class="search-results">
          <div 
            v-for="term in ddaTermModalData.searchResults" 
            :key="term.id"
            class="search-result-item"
            @click="selectDDATerm(term)"
            :class="{ selected: ddaTermModalData.selectedDDATerm?.id === term.id }"
          >
            <strong>{{ term.term }}</strong>
            <span v-if="term.description" class="term-description">{{ term.description }}</span>
          </div>
        </div>
        
        <div v-if="ddaTermModalData.selectedDDATerm" class="selected-term">
          <h4>Selected Term:</h4>
          <div class="term-details">
            <strong>{{ ddaTermModalData.selectedDDATerm.term }}</strong>
            <p v-if="ddaTermModalData.selectedDDATerm.description">{{ ddaTermModalData.selectedDDATerm.description }}</p>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeDDATermModal">Cancel</button>
        <button 
          class="btn btn-primary" 
          @click="saveDDATermSelection"
          :disabled="!ddaTermModalData.selectedDDATerm"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint no-undef: 0 */
import { computed, onMounted, reactive, ref, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from "@/stores/auth_store";
import { useErrorStore } from "@/stores/error_store";
import { useDropdownStore } from "@/stores/dropdown_store";
import { axiosQuery, currencyFormat, percentFormat } from "@/assets/js/utils";
import axios from 'axios';
import { OWNER_ALLOCATIONS, CONSULTANT_TYPES } from "@/types/Constants";
import CompanyItem from '@/components/mydata/CompanyItem.vue';
import EditableCompanyItem from '@/components/mydata/EditableCompanyItem.vue';
import AgGrid from "../../components/AgGrid.vue";

// Route and stores
const route = useRoute();
const authStore = useAuthStore();
const errorStore = useErrorStore();
const dropdownStore = useDropdownStore();

// Handle consultant updates from EditableCompanyItem
function handleConsultantUpdate(updateData) {
  console.log('📝 Consultant updated:', updateData);
  
  // Update the local data
  const { type, companies } = updateData;
  
  // Map title case consultant types (from EditableCompanyItem) to data keys
  const mapping = {
    // Title case from EditableCompanyItem props.title
    'Interior Design': 'interior_designers',
    'Structural Engineer': 'structural_engineers', 
    'Civil Engineer': 'civil_engineers',
    'Mechanical Engineer': 'mechanical_engineers',
    'MEP Engineer': 'mechanical_engineers',
    'Mechanical': 'mechanical_engineers',
    'Electrical Engineer': 'electrical_engineers',
    'Electrical': 'electrical_engineers',
    'Plumbing Engineer': 'plumbing_engineers',
    'Plumbing': 'plumbing_engineers',
    'Fire Protection Engineer': 'fire_protection_engineers',
    'Fire Protection': 'fire_protection_engineers',
    'Environmental Engineer': 'environmental_engineers',
    'Environmental': 'environmental_engineers',
    'Geotechnical Engineer': 'geotechnical_engineers',
    'Design Architect': 'design_architects',
    'Architect of Record': 'architects',
    'Landscape Architect': 'landscape_architects',
    "Owner's Representative": 'owners_reps',
    
    // Legacy snake_case support (in case some still use this format)
    'design_architect': 'design_architects',
    'interior_design': 'interior_designers',
    'structural_engineer': 'structural_engineers',
    'mep_engineer': 'mechanical_engineers',
    'civil_engineer': 'civil_engineers',
    'geotechnical_engineer': 'geotechnical_engineers',
    'electrical_engineer': 'electrical_engineers',
    'plumbing_engineer': 'plumbing_engineers',
    'fire_protection_engineer': 'fire_protection_engineers',
    'environmental_engineer': 'environmental_engineers'
  };
  
  const dataKey = mapping[type];
  if (dataKey && consultantTypesData.value[dataKey] !== undefined) {
    consultantTypesData.value[dataKey] = companies;
    console.log(`✅ Updated ${dataKey}:`, companies);
  } else {
    console.error(`❌ Unknown data key: ${dataKey} for type: ${type}`);
    console.error(`❌ Available mapping keys:`, Object.keys(mapping));
    console.error(`❌ Available data keys:`, Object.keys(consultantTypesData.value));
  }
}

// Handle investor/sponsor updates from EditableCompanyItem
function handleInvestorUpdate(updateData) {
  console.log('📝 Investor/Sponsor updated:', updateData);
  
  // Update the local data
  const { type, companies } = updateData;
  const mapping = {
    'Debt': 'lenders',
    'Equity': 'investors',
    'Developer(s)': 'developers'
  };
  
  const dataKey = mapping[type];
  if (dataKey && investorSponsorTypes.value[dataKey] !== undefined) {
    // Convert companies to the proper format for display
    // Handle both company objects and IDs
    const processedCompanies = companies.map(company => {
      if (typeof company === 'string') {
        // If it's just an ID, convert to object
        return { id: company, name: company, investor_sponsor_id: company };
      } else if (company && typeof company === 'object') {
        // If it's already an object, ensure it has the right structure
        return {
          id: company.id || company.investor_sponsor_id,
          name: company.name,
          investor_sponsor_id: company.id || company.investor_sponsor_id,
          ...company
        };
      }
      return company;
    }).filter(Boolean);
    
    investorSponsorTypes.value[dataKey] = processedCompanies;
    console.log(`✅ Updated ${dataKey}:`, processedCompanies);
  } else {
    console.error(`❌ Unknown data key: ${dataKey} for type: ${type}`);
  }
}

// API Configuration

const apiBase = process.env.VUE_APP_ENV_API_BASE || "http://159.203.115.94:3000/api/v1/";
let authToken = reactive({});
let selectedId = ref();

// Data refs
let projectInformation = ref(null);
let projectGC = ref(null);
let ownerAllocationData = ref([]);
let consultantsData = ref([]);
let contractorPerformanceData = ref([]);
let contractorPerformanceDataCtr = ref(0);
let ddaTerms = ref([]); // Store all DDA terms for dropdown

// DDA Term Modal Data (copied from ProjectSummary)
let ddaTermModalData = ref({
  isOpen: false,
  rowData: null,
  fieldName: '',
  currentValue: '',
  searchTerm: '',
  searchResults: [],
  showDropdown: false,
  selectedDDATerm: null,
  allDDATerms: []
});

// Computed property for data loading state
let isDataLoaded = computed(() => {
  return projectInformation.value !== null;
});

// Investor/Sponsor data structure
let investorSponsorTypes = ref({
  lenders: [],
  investors: [],
  developers: []
});

// Consultant data structure  
let consultantTypesData = ref({
  subcontracts: [],
  owners_reps: [],
  third_party_pm: [],
  architects: [],
  design_architects: [],
  civil_engineers: [],
  structural_engineers: [],
  mechanical_engineers: [],
  electrical_engineers: [],
  plumbing_engineers: [],
  fire_protection_engineers: [],
  interior_designers: [],
  landscape_architects: [],
  geotechnical_engineers: [],
  environmental_engineers: [],
});

// GC Edit Modal state
const gcEditModal = ref({
  isOpen: false,
  searchTerm: '',
  searchResults: [],
  showDropdown: false,
  selectedContractor: null,
  allContractors: []
});

// AG-Grid column definitions for contractor performance
const contractorPerformanceColumnDefs = [
  {
    headerName: 'Contractor',
    field: 'contractor_name',
    pinned: 'left',
    width: 200,
    cellStyle: { fontWeight: 'bold' }
  },
        {
        headerName: 'DDA Term',
        field: 'dda_term_name',
        width: 300,
        hide: true, // HIDDEN: Application Details column now shows G703 terms
        editable: false, // Make it non-editable, use click instead
        valueGetter: p => {
          // Priority: 1) G703 DDA Term (from match), 2) dda_term_name, 3) Not Assigned
          if (p.data?.g703_dda_term) {
            return p.data.g703_dda_term;
          }
          return p.data?.dda_term_name || p.data?.dda_term || 'Not Assigned';
        },
        cellStyle: { 
          'white-space': 'normal', 
          'line-height': '1.3',
          'cursor': 'pointer',
          'background-color': '#f8f9fa',
          'border': '1px solid #dee2e6'
        },
        tooltipField: 'dda_term_name',
        onCellClicked: (params) => {
          if (params.data) {
            handleEditDDATerm(params.data, 'dda_term_name', params.data?.dda_term_name);
          }
        }
      },
  {
    headerName: 'Application Details',
    field: 'description',
    width: 250,
    valueGetter: p => {
      // Priority: 1) G703 DDA Term (from match), 2) G703 Description, 3) Scope Detail, 4) Description
      if (p.data?.g703_dda_term) {
        return p.data.g703_dda_term;
      }
      if (p.data?.g703_description) {
        return p.data.g703_description;
      }
      return p.data?.scope_detail || p.data?.description || 'General Work';
    },
    tooltipField: 'description',
    wrapText: true,
    autoHeight: true,
    cellStyle: { 'white-space': 'normal', 'line-height': '1.3' }
  },
  {
    headerName: 'Performance',
    headerClass: 'center-align-header',
    children: [
      {
        headerName: 'Budget',
        field: 'current_budget',
        width: 120,
        valueFormatter: params => currencyFormat(params.value || 0),
        type: 'rightAligned'
      },
      {
        headerName: 'Billed to Date',
        field: 'total_completed_to_date',
        width: 130,
        valueFormatter: params => currencyFormat(params.value || 0),
        type: 'rightAligned'
      },
      {
        headerName: '% Complete',
        field: 'total_completed_to_date_pct',
        width: 110,
        valueFormatter: params => {
          const value = params.value || 0;
          return value.toFixed(1) + '%';
        },
        type: 'rightAligned'
      },
      {
        headerName: 'Variance $',
        field: 'budget_variance',
        width: 120,
        valueFormatter: params => currencyFormat(params.value || 0),
        type: 'rightAligned',
        cellStyle: params => {
          const value = params.value || 0;
          return {
            color: value < 0 ? '#d32f2f' : value > 0 ? '#2e7d32' : '#333'
          };
        }
      },
      {
        headerName: 'Variance %',
        field: 'budget_variance_pct',
        width: 110,
        valueFormatter: params => percentFormat(params.value || 0),
        type: 'rightAligned',
        cellStyle: params => {
          const value = params.value || 0;
          return {
            color: value < 0 ? '#d32f2f' : value > 0 ? '#2e7d32' : '#333'
          };
        }
      }
    ]
  }
];

// API Functions
async function fetchProjectInformation() {
  return new Promise((resolve) => {
    axiosQuery(
      `${apiBase}projects/${selectedId.value}`,
      authToken,
      (data) => {
        projectInformation.value = data;
        // Extract GC information from project data
        if (data.contractor_id && data.project_general_contractor) {
          projectGC.value = {
            id: data.contractor_id,
            name: data.project_general_contractor
          };
        }
        resolve(projectInformation.value.id);
      },
      errorStore,
      'Error fetching project_id' + selectedId.value
    );
  });
}

function fetchOwnerAllocation() {
  axiosQuery(
    `${apiBase}owner_allocations/fetch_project_data?project_ids=${selectedId.value}`,
    authToken,
    (data) => {
      ownerAllocationData.value = data;
      data.forEach(item => {
        const investmentRole = item.investment_role_id_s;
        // Pass the full sponsor object with ID and name
        const sponsorObj = {
          id: item.investor_sponsor_id,
          name: item.investor_sponsor_id_s,
          investor_sponsor_id: item.investor_sponsor_id
        };
        addSponsorIdToRole(investmentRole, sponsorObj);
      });
    },
    errorStore
  );
}

function fetchConsultants() {
  // First, get all consultants directly from the consultants table (like Directory does)
  axiosQuery(
    `${apiBase}consultants`,
    authToken,
    (allConsultants) => {
      console.log('🔍 All consultants from direct API:', allConsultants);
      
      // Then get the consultant project relationships
      axiosQuery(
        `${apiBase}consultant_projects/fetch_project_data?project_ids=${selectedId.value}`,
        authToken,
        (data) => {
          consultantsData.value = data;
          console.log('🔍 Consultant projects data:', data);
          
          data.forEach(item => {
            const consultantRole = item.consultant_type_id_s;
            let consultantObj = null;
            
            // If consultant_id exists, find the full consultant object
            if (item.consultant_id && allConsultants) {
              consultantObj = allConsultants.find(c => c.id === item.consultant_id);
              if (consultantObj) {
                console.log(`✅ Found consultant: ${consultantObj.name} (ID: ${consultantObj.id}) for role ${consultantRole}`);
                addConsultant(consultantRole, consultantObj);
              } else {
                console.log(`⚠️ Consultant ID ${item.consultant_id} not found in allConsultants for role ${consultantRole}`);
              }
            } else {
              console.log(`⚠️ No consultant_id for consultant project ${item.id}, role: ${consultantRole}`);
            }
          });
        },
        errorStore
      );
    },
    errorStore
  );
}

function fetchContractorPerformance() {
  axiosQuery(
    `${apiBase}projects/fetch_project_g703?project_ids=${selectedId.value}`,
    authToken,
    (response) => {
      // Use G702 contractor data specifically for Companies tab
      let data = [];
      
      if (Array.isArray(response)) {
        // Legacy format - try to find contractor data
        data = response;
      } else if (response && (response.subcontractor_g702s || response.extracted_g702s)) {
        // EXACT COPY of G702 grid logic
        const subs = response.subcontractor_g702s || [];
        const extracted = response.extracted_g702s || [];
        console.log(`🔥 G702 DATA: Found ${subs.length} subcontractor G702s + ${extracted.length} extracted G702s`);
        
        // Combine traditional subcontractor data with extracted contractor data (EXACT copy)
        const allContractors = [...subs, ...extracted];
        
        // Use ALL data without draw filtering for Companies tab
        const combinedData = [...subs, ...extracted];
        
        // Sort like G702 grid: by contractor name, then by date (newest first)
        data = combinedData.sort((a, b) => {
          const nameA = (a.contractor_name || '').toLowerCase();
          const nameB = (b.contractor_name || '').toLowerCase();
          if (nameA !== nameB) {
            return nameA.localeCompare(nameB);
          }
          const dateA = new Date(a.period_through || a.period_to || '1900-01-01');
          const dateB = new Date(b.period_through || b.period_to || '1900-01-01');
          return dateB - dateA; // Newest first
        });
        
        console.log('📊 Using EXACT G702 grid logic:', data.length, 'total contractors');
      } else if (response && response.all_contractors) {
        // Fallback: Use all_contractors if available
        data = response.all_contractors;
        console.log('📊 Fallback to all_contractors:', data.length, 'contractors');
      } else {
        console.error('❌ No contractor data found in response:', response);
        data = [];
      }
      
      console.log('📊 Processing G702 contractor performance data:', data.length, 'records');
      console.log('🔍 Sample G702 contractor record:', data[0]);
      
      // Group data by contractor and aggregate performance metrics  
      const contractorMap = new Map();
      
      data.forEach(record => {
        // Extract contractor names from G702 contractor data
        const contractorName = record.contractor_name || 'Unknown Contractor';
        
        // Skip if no contractor name
        if (!contractorName || contractorName === '' || contractorName === 'Unknown Contractor') {
          return;
        }
        
        if (!contractorMap.has(contractorName)) {
          contractorMap.set(contractorName, {
            contractor_name: contractorName,
            contractor_type: record.contractor_type || 'Contractor',
            descriptions: new Set(),
            records: [],
            applications: new Set()
          });
        }
        
        const contractor = contractorMap.get(contractorName);
        
        // Collect all records to find latest by period_through
        // PRIORITY: Use G703 DDA term from match, then G703 description, then scope_detail
        const scopeDescription = record.g703_dda_term || record.g703_description || record.scope_detail || `${record.contractor_type} Work`;
        contractor.descriptions.add(scopeDescription);
        // Use application_number for G702/G703 correlation (draw_number doesn't exist on SubcontractorG702)
        contractor.applications.add(record.application_number || 'N/A');
        contractor.records.push(record);
      });
      
      // Convert to array format for AG-Grid using correct G702 fields
      const performanceData = Array.from(contractorMap.values()).map(contractor => {
        // Find the BEST record: prioritize records with actual financial data, then by date
        const latestRecord = contractor.records.reduce((best, current) => {
          // Score records based on data completeness and recency
          const scoreRecord = (record) => {
            let score = 0;
            
            // High priority: Has actual earned amount
            if (parseFloat(record.total_earned_less_retainage || 0) > 0) score += 1000;
            if (parseFloat(record.total_completed_and_stored_to_date || 0) > 0) score += 800;
            
            // Medium priority: Has contract amounts
            if (parseFloat(record.contract_sum_to_date || 0) > 0) score += 500;
            if (parseFloat(record.original_contract_sum || 0) > 0) score += 300;
            
            // Low priority: Has balance data
            if (parseFloat(record.balance_to_finish_including_retainage || 0) > 0) score += 100;
            
            // Date bonus (more recent = higher score)
            const date = record.period_through ? new Date(record.period_through) : new Date(0);
            score += Math.min(date.getTime() / 1000000, 100); // Max 100 points for date
            
            return score;
          };
          
          return scoreRecord(current) > scoreRecord(best) ? current : best;
        }, contractor.records[0]);
        
        console.log(`🔍 ${contractor.contractor_name} latest record:`, latestRecord);
        
        // Use latest record for all financial metrics
        const currentBudget = parseFloat(latestRecord.contract_sum_to_date || latestRecord.original_contract_sum || 0);
        const balanceToFinish = parseFloat(latestRecord.balance_to_finish_including_retainage || 0);
        
        // Use the CORRECT field that the G702 grid uses: total_earned_less_retainage
        let completedToDate = parseFloat(latestRecord.total_earned_less_retainage || 0);
        
        // If that's also 0, try total_completed_and_stored_to_date
        if (completedToDate === 0) {
          completedToDate = parseFloat(latestRecord.total_completed_and_stored_to_date || 0);
        }
        
        // Last resort: calculate from budget - balance
        if (completedToDate === 0 && currentBudget > 0 && balanceToFinish >= 0) {
          completedToDate = currentBudget - balanceToFinish;
        }
        
        console.log(`💰 ${contractor.contractor_name} ALL G702 fields:`, {
          contract_sum_to_date: latestRecord.contract_sum_to_date,
          original_contract_sum: latestRecord.original_contract_sum,
          total_completed_and_stored_to_date: latestRecord.total_completed_and_stored_to_date,
          balance_to_finish_including_retainage: latestRecord.balance_to_finish_including_retainage,
          retainage: latestRecord.retainage,
          current_payment_due: latestRecord.current_payment_due,
          total_earned_less_retainage: latestRecord.total_earned_less_retainage,
          net_change_by_change_order: latestRecord.net_change_by_change_order
        });
        
        console.log(`💰 ${contractor.contractor_name} CALCULATION:`, {
          currentBudget,
          balanceToFinish,
          'completedToDate (budget - balance)': completedToDate
        });
        const originalBudget = parseFloat(latestRecord.original_contract_sum || 0);
        const budgetVariance = currentBudget - originalBudget;
        const budgetVariancePct = originalBudget > 0 ? (budgetVariance / originalBudget) * 100 : 0;
        const completionPct = currentBudget > 0 ? (completedToDate / currentBudget) * 100 : 0;
        
        console.log(`🧮 ${contractor.contractor_name} FINAL CALCULATIONS:`, {
          currentBudget,
          balanceToFinish,
          completedToDate,
          completionPct: completionPct.toFixed(1) + '%',
          budgetVariance: budgetVariance.toFixed(2),
          budgetVariancePct: budgetVariancePct.toFixed(1) + '%'
        });
        
        // Create clean description summary
        const descriptions = Array.from(contractor.descriptions);
        let cleanDescription;
        if (descriptions.length === 1) {
          cleanDescription = descriptions[0];
        } else if (descriptions.length <= 3) {
          cleanDescription = descriptions.join(', ');
        } else {
          cleanDescription = descriptions.slice(0, 2).join(', ') + ` and ${descriptions.length - 2} other scopes`;
        }
        
        // Truncate if too long
        if (cleanDescription.length > 150) {
          cleanDescription = cleanDescription.substring(0, 147) + '...';
        }
        
        // Auto-match DDA term based on contractor name and scope
        const autoMatchedTerm = autoMatchContractorToDDA(contractor.contractor_name, cleanDescription);
        
        return {
          contractor_name: `${contractor.contractor_name} (${contractor.contractor_type})`,
          dda_term_name: autoMatchedTerm || 'Not Assigned',
          description: cleanDescription,
          current_budget: currentBudget,
          total_completed_to_date: completedToDate,
          original_budget: parseFloat(latestRecord.original_contract_sum || 0),
          total_completed_to_date_pct: completionPct,
          budget_variance: budgetVariance,
          budget_variance_pct: budgetVariancePct,
          record_count: contractor.records.length,
          applications: Array.from(contractor.applications).join(', '),
          trade_count: contractor.descriptions.size,
          period_through: latestRecord.period_through,
          application_number: latestRecord.application_number,
          latest_record: latestRecord // Store for DDA term editing
        };
      });
      
      // Sort by budget descending
      contractorPerformanceData.value = performanceData.sort((a, b) => b.current_budget - a.current_budget);
      contractorPerformanceDataCtr.value++;
      
      console.log('✅ Processed G702 contractor performance data:', contractorPerformanceData.value.length, 'contractors with real metrics');
    },
    errorStore
  );
}

// Load DDA terms for dropdown
async function loadDDATerms() {
  try {
    console.log('📥 Loading DDA terms for contractor dropdown...');
    const response = await axios.get(`${apiBase}dda_terms`, authToken.value);
    
    if (response.data && Array.isArray(response.data)) {
      ddaTerms.value = response.data.map(term => ({
        id: term.id,
        term: term.term || term.name,
        description: term.description || '',
        category: term.category || '',
        dda_term_category_id: term.dda_term_category_id
      }));
      console.log('✅ Loaded DDA terms for contractor dropdown:', ddaTerms.value.length);
      
      // Update column definition with DDA term options
      updateDDATermDropdown();
    } else {
      console.warn('⚠️ API returned non-array DDA terms data:', response.data);
    }
  } catch (error) {
    console.error('❌ Error loading DDA terms:', error);
  }
}

// Update the DDA term column dropdown options
function updateDDATermDropdown() {
  const ddaTermNames = ['Not Assigned', ...ddaTerms.value.map(term => term.term)];
  
  // Find and update the DDA term column
  const ddaColumn = contractorPerformanceColumnDefs.find(col => col.field === 'dda_term_name');
  if (ddaColumn && ddaColumn.cellEditorParams) {
    ddaColumn.cellEditorParams.values = ddaTermNames;
    console.log('✅ Updated DDA term dropdown with', ddaTermNames.length, 'options');
  }
}

// Auto-match contractor to DDA term based on name and scope
function autoMatchContractorToDDA(contractorName, description) {
  const name = contractorName.toLowerCase();
  const desc = description.toLowerCase();
  
  // Define contractor type patterns and their DDA term mappings
  const contractorPatterns = {
    // Masonry
    'masonry': 'Masonry',
    'mason': 'Masonry',
    'brick': 'Masonry',
    'stone': 'Masonry',
    
    // Welding
    'welding': 'Structural Steel',
    'steel': 'Structural Steel',
    'metal': 'Structural Steel',
    
    // Scaffolding
    'scaffolding': 'General Requirements',
    'scaffold': 'General Requirements',
    
    // Plastering
    'plastering': 'Interior Finishes',
    'plaster': 'Interior Finishes',
    
    // Management/General
    'management': 'General Requirements',
    'general contractor': 'General Requirements',
    'project management': 'General Requirements',
    
    // Mechanical
    'mechanical': 'HVAC',
    'hvac': 'HVAC',
    'plumbing': 'Plumbing',
    
    // Electrical
    'electrical': 'Electrical',
    'electric': 'Electrical',
    
    // Industries/Manufacturing
    'industries': 'Trade Costs Total',
    'manufacturing': 'Trade Costs Total'
  };
  
  // Check contractor name first
  for (const [pattern, ddaTerm] of Object.entries(contractorPatterns)) {
    if (name.includes(pattern)) {
      console.log(`🎯 Auto-matched contractor "${contractorName}" to DDA term "${ddaTerm}" via name pattern "${pattern}"`);
      return ddaTerm;
    }
  }
  
  // Check description as fallback
  for (const [pattern, ddaTerm] of Object.entries(contractorPatterns)) {
    if (desc.includes(pattern)) {
      console.log(`🎯 Auto-matched contractor "${contractorName}" to DDA term "${ddaTerm}" via description pattern "${pattern}"`);
      return ddaTerm;
    }
  }
  
  // Default based on contractor type
  if (name.includes('subcontractor')) {
    return 'Trade Costs Total';
  } else if (name.includes('general')) {
    return 'General Requirements';
  }
  
  console.log(`❓ No auto-match found for contractor "${contractorName}"`);
  return null;
}

// Helper functions
function addSponsorIdToRole(investmentRole, sponsorObj) {
  const roleMapping = {
    [OWNER_ALLOCATIONS.LENDER]: 'lenders',
    [OWNER_ALLOCATIONS.INVESTOR]: 'investors',
    [OWNER_ALLOCATIONS.DEVELOPER]: 'developers'
  };
  const arrayName = roleMapping[investmentRole];
  if (arrayName) {
    // sponsorObj already has {id, name, investor_sponsor_id}
    
    // Check if this company is already in the array
    const existingIndex = investorSponsorTypes.value[arrayName].findIndex(
      item => (item.id || item) === sponsorObj.id || (item.name || item) === sponsorObj.name
    );
    
    if (existingIndex === -1) {
      investorSponsorTypes.value[arrayName].push(sponsorObj);
    }
  }
}

function addConsultant(consultantRole, consultantObj) {
  // Map consultant_type_id (number) to data array key
  const roleMapping = {
    // ID-based mapping from backend consultant_type_id
    355: 'civil_engineers',         // Civil Engineer
    356: 'structural_engineers',    // Structural Engineer
    357: 'mechanical_engineers',    // MEP/Mechanical Engineer
    358: 'electrical_engineers',    // Electrical Engineer
    359: 'plumbing_engineers',      // Plumbing Engineer
    360: 'fire_protection_engineers', // Fire Protection Engineer
    361: 'owners_reps',             // Owner's Representative
    362: 'design_architects',       // Design Architect
    363: 'architects',              // Architect of Record
    364: 'interior_designers',      // Interior Design
    365: 'landscape_architects',    // Landscape Architect
    366: 'geotechnical_engineers',  // Geotechnical Engineer
    367: 'environmental_engineers', // Environmental Engineer
    368: 'third_party_pm',          // Third Party PM
    369: 'subcontracts',            // Subcontractor
    
    // Legacy string-based support (if still used)
    [CONSULTANT_TYPES.SUBCONTRACTOR]: 'subcontracts',
    [CONSULTANT_TYPES.OWNERS_REPRESENTATIVE]: 'owners_reps',
    [CONSULTANT_TYPES.THIRD_PARTY_PROJECT_MANAGER]: 'third_party_pm',
    [CONSULTANT_TYPES.ARCHITECT]: 'architects',
    [CONSULTANT_TYPES.DESIGN_ARCHITECT]: 'design_architects',
    [CONSULTANT_TYPES.CIVIL_ENGINEER]: 'civil_engineers',
    [CONSULTANT_TYPES.STRUCTURAL_ENGINEER]: 'structural_engineers',
    [CONSULTANT_TYPES.MECHANICAL_ENGINEER]: 'mechanical_engineers',
    [CONSULTANT_TYPES.ELECTRICAL_ENGINEER]: 'electrical_engineers',
    [CONSULTANT_TYPES.PLUMBING_ENGINEER]: 'plumbing_engineers',
    [CONSULTANT_TYPES.FIRE_PROTECTION_ENGINEER]: 'fire_protection_engineers',
    [CONSULTANT_TYPES.INTERIOR_DESIGN]: 'interior_designers',
    [CONSULTANT_TYPES.LANDSCAPE_ARCHITECT]: 'landscape_architects',
    [CONSULTANT_TYPES.GEOTECHNICAL_ENGINEER]: 'geotechnical_engineers',
    [CONSULTANT_TYPES.ENVIRONMENTAL_ENGINEER]: 'environmental_engineers'
  };
  const arrayName = roleMapping[consultantRole];
  if (arrayName && consultantObj) {
    // Check if consultant already exists by ID
    const exists = consultantTypesData.value[arrayName].find(c => c.id === consultantObj.id);
    if (!exists) {
      consultantTypesData.value[arrayName].push(consultantObj);
      console.log(`✅ Added consultant ${consultantObj.name} to ${arrayName} (role ID: ${consultantRole})`);
    }
  } else {
    console.error(`❌ Unknown consultant role: ${consultantRole}, available mappings:`, Object.keys(roleMapping));
  }
}

// GC Edit Modal Functions
async function editGeneralContractor() {
  // Load contractors if not already loaded
  if (!dropdownStore.generalContractors || dropdownStore.generalContractors.length === 0) {
    console.log('📥 Loading contractors for GC selection...');
    await dropdownStore.getGeneralContractors();
  }
  
  gcEditModal.value = {
    isOpen: true,
    searchTerm: projectGC.value?.name || '',
    searchResults: [...(dropdownStore.generalContractors || [])],
    showDropdown: true,
    selectedContractor: projectGC.value,
    allContractors: [...(dropdownStore.generalContractors || [])]
  };
  
  // Focus search input will be handled by the input element
}

function searchContractors() {
  const searchTerm = gcEditModal.value.searchTerm.toLowerCase();
  
  if (!searchTerm) {
    gcEditModal.value.searchResults = [...gcEditModal.value.allContractors];
    gcEditModal.value.showDropdown = true;
    return;
  }
  
  const filtered = gcEditModal.value.allContractors.filter(contractor =>
    contractor.name.toLowerCase().includes(searchTerm)
  );
  
  gcEditModal.value.searchResults = filtered;
  gcEditModal.value.showDropdown = true;
}

function selectContractor(contractor) {
  gcEditModal.value.selectedContractor = contractor;
  gcEditModal.value.searchTerm = contractor.name;
  gcEditModal.value.showDropdown = false;
}

function closeGCEditModal() {
  gcEditModal.value.isOpen = false;
  gcEditModal.value.searchTerm = '';
  gcEditModal.value.searchResults = [];
  gcEditModal.value.showDropdown = false;
  gcEditModal.value.selectedContractor = null;
}

async function saveGeneralContractor() {
  if (!gcEditModal.value.selectedContractor) return;
  
  const updateData = {
    contractor_id: gcEditModal.value.selectedContractor.id
  };
  
  try {
    const response = await fetch(`${apiBase}projects/${selectedId.value}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...authToken.value
      },
      body: JSON.stringify({ project: updateData })
    });
    
    if (response.ok) {
      projectGC.value = {
        id: gcEditModal.value.selectedContractor.id,
        name: gcEditModal.value.selectedContractor.name
      };
      
      console.log('✅ Updated project GC:', projectGC.value.name);
      closeGCEditModal();
    } else {
      console.error('❌ Failed to update GC:', response.status);
      errorStore.setError('Failed to update General Contractor');
    }
  } catch (error) {
    console.error('❌ Error updating GC:', error);
    errorStore.setError('Error updating General Contractor');
  }
}

// AG-Grid event handlers
function onContractorPerformanceGridReady(params) {
  console.log('🔧 Contractor Performance AG-Grid ready');
}

// DDA Term Modal Functions (copied from ProjectSummary)
async function handleEditDDATerm(rowData, fieldName, currentValue) {
  try {
    console.log('🔥 EDIT DDA TERM - Opening modal for:', rowData, fieldName, currentValue);
    
    // Load DDA terms using the correct API endpoint
    let allDDATerms = [];
    try {
      console.log('📥 Loading DDA terms from API...');
      const response = await axios.get(`${apiBase}dda_terms`, authToken.value);
      
      if (response.data && Array.isArray(response.data)) {
        allDDATerms = response.data.map(term => ({
          id: term.id,
          term: term.term || term.name,
          description: term.description || '',
          category: term.category || '',
          dda_term_category_id: term.dda_term_category_id,
          type: 'dda_term'
        }));
        console.log('✅ DDA terms loaded from API:', allDDATerms.length);
        
        // Store DDA terms for future use
        ddaTerms.value = allDDATerms;
      } else {
        console.warn('⚠️ API returned non-array DDA terms data:', response.data);
      }
    } catch (error) {
      console.error('❌ Error loading DDA terms from API:', error);
      return;
    }
    
    ddaTermModalData.value = {
      isOpen: true,
      rowData: rowData,
      fieldName: fieldName,
      currentValue: currentValue || '',
      searchTerm: '',
      searchResults: [],
      showDropdown: false,
      selectedDDATerm: null,
      allDDATerms: allDDATerms
    };
    
    // Try to auto-match based on contractor name and description using AI
    const description = rowData.contractor_name + ' ' + (rowData.scope_detail || rowData.description || '');
    console.log('🎯 Auto-matching DDA term for contractor:', description);
    
    if (description) {
      // Use AI auto-matching like G703 grid (but simpler for contractors)
      const autoMatch = await findBestDDATermForContractor(description, allDDATerms);
      if (autoMatch) {
        console.log('✅ AI auto-matched DDA term:', autoMatch.term);
        ddaTermModalData.value.selectedDDATerm = autoMatch;
        ddaTermModalData.value.searchTerm = autoMatch.term;
      } else if (currentValue) {
        // Fallback to current value
        const exactMatch = allDDATerms.find(term =>
          term.term.toLowerCase() === currentValue.toLowerCase()
        );
        if (exactMatch) {
          ddaTermModalData.value.selectedDDATerm = exactMatch;
          ddaTermModalData.value.searchTerm = exactMatch.term;
        } else {
          ddaTermModalData.value.searchTerm = currentValue;
        }
      }
    } else if (currentValue) {
      // No description, just use current value
      const exactMatch = allDDATerms.find(term =>
        term.term.toLowerCase() === currentValue.toLowerCase()
      );
      if (exactMatch) {
        ddaTermModalData.value.selectedDDATerm = exactMatch;
        ddaTermModalData.value.searchTerm = exactMatch.term;
      } else {
        ddaTermModalData.value.searchTerm = currentValue;
      }
    }
    
  } catch (error) {
    console.error('❌ Error in handleEditDDATerm:', error);
  }
}

function closeDDATermModal() {
  ddaTermModalData.value.isOpen = false;
}

function saveDDATermSelection() {
  if (ddaTermModalData.value.selectedDDATerm && ddaTermModalData.value.rowData) {
    // Update the row data
    ddaTermModalData.value.rowData.dda_term_name = ddaTermModalData.value.selectedDDATerm.term;
    ddaTermModalData.value.rowData.dda_term_id = ddaTermModalData.value.selectedDDATerm.id;
    
    console.log('💾 Saved DDA term:', ddaTermModalData.value.selectedDDATerm.term, 'for contractor:', ddaTermModalData.value.rowData.contractor_name);
    
    // Refresh the grid
    contractorPerformanceDataCtr.value++;
    
    // Close modal
    closeDDATermModal();
  }
}

function filterDDATerms() {
  const searchTerm = ddaTermModalData.value.searchTerm.toLowerCase();
  if (searchTerm.length < 2) {
    ddaTermModalData.value.searchResults = [];
    return;
  }
  
  const filtered = ddaTermModalData.value.allDDATerms
    .filter(term => 
      term.term.toLowerCase().includes(searchTerm) ||
      (term.description && term.description.toLowerCase().includes(searchTerm))
    )
    .slice(0, 10); // Limit results
    
  ddaTermModalData.value.searchResults = filtered;
}

function selectDDATerm(term) {
  ddaTermModalData.value.selectedDDATerm = term;
  ddaTermModalData.value.searchTerm = term.term;
  ddaTermModalData.value.searchResults = [];
}

// AI-powered DDA term matching for contractors
async function findBestDDATermForContractor(description, allDDATerms) {
  if (!description || !allDDATerms || allDDATerms.length === 0) return null;
  
  try {
    // Enhanced contractor-specific matching logic
    const desc = description.toLowerCase();
    
    // Define contractor-specific DDA term mappings
    const contractorMappings = {
      'masonry': ['Masonry', 'Brickwork', 'Stone Work', 'Block Work'],
      'welding': ['Structural Steel', 'Steel Work', 'Welding', 'Ironwork'],
      'scaffolding': ['General Requirements', 'Safety Equipment', 'Temporary Construction'],
      'plumbing': ['Plumbing', 'Plumbing Systems', 'Water Systems'],
      'electrical': ['Electrical', 'Electrical Systems', 'Power Systems'],
      'hvac': ['HVAC', 'Mechanical Systems', 'Heating', 'Ventilation', 'Air Conditioning'],
      'roofing': ['Roofing', 'Roof Systems', 'Waterproofing'],
      'concrete': ['Concrete', 'Concrete Work', 'Cast-in-Place Concrete'],
      'drywall': ['Drywall', 'Gypsum Board', 'Interior Partitions'],
      'flooring': ['Flooring', 'Floor Finishes', 'Carpet', 'Tile'],
      'painting': ['Painting', 'Finishes', 'Coatings'],
      'windows': ['Windows', 'Glazing', 'Curtain Wall'],
      'insulation': ['Insulation', 'Thermal Protection'],
      'demolition': ['Demolition', 'Site Preparation', 'Selective Demolition'],
      'excavation': ['Excavation', 'Earthwork', 'Site Work'],
      'management': ['General Requirements', 'Project Management', 'Construction Management'],
      'general': ['General Requirements', 'Project Management']
    };
    
    // Find matching terms based on contractor type
    let potentialTerms = [];
    for (const [keyword, terms] of Object.entries(contractorMappings)) {
      if (desc.includes(keyword)) {
        for (const termName of terms) {
          const matchingTerms = allDDATerms.filter(term => 
            term.term.toLowerCase().includes(termName.toLowerCase()) ||
            termName.toLowerCase().includes(term.term.toLowerCase())
          );
          potentialTerms.push(...matchingTerms);
        }
      }
    }
    
    // Remove duplicates and score by relevance
    const uniqueTerms = [...new Map(potentialTerms.map(term => [term.id, term])).values()];
    
    if (uniqueTerms.length > 0) {
      // Score terms by how well they match
      const scoredTerms = uniqueTerms.map(term => {
        let score = 0;
        const termLower = term.term.toLowerCase();
        
        // Higher score for exact contractor type matches
        Object.entries(contractorMappings).forEach(([keyword, terms]) => {
          if (desc.includes(keyword)) {
            terms.forEach(termName => {
              if (termLower.includes(termName.toLowerCase())) {
                score += 10;
              }
            });
          }
        });
        
        // Bonus for common construction terms
        if (termLower.includes('work') || termLower.includes('system')) score += 2;
        
        return { ...term, score, confidence: Math.min(score / 10, 0.9) };
      });
      
      // Return the highest scoring term
      const bestTerm = scoredTerms.sort((a, b) => b.score - a.score)[0];
      
      if (bestTerm.confidence > 0.3) {
        console.log(`🤖 AI matched "${description}" → "${bestTerm.term}" (confidence: ${bestTerm.confidence.toFixed(2)})`);
        return {
          id: bestTerm.id,
          term: bestTerm.term,
          description: bestTerm.description,
          confidence: bestTerm.confidence
        };
      }
    }
    
    console.log(`🤖 No good AI match found for "${description}"`);
    return null;
    
  } catch (error) {
    console.error('❌ Error in AI contractor matching:', error);
    return null;
  }
}

// Component lifecycle
onMounted(async () => {
  authToken.value = authStore.getAuthTokenHeader;
  selectedId.value = route.params.selectedId;
  
  await fetchProjectInformation();
  await fetchOwnerAllocation();
  await fetchConsultants();
  await loadDDATerms(); // Load DDA terms first
  await fetchContractorPerformance();
});
</script>

<style scoped lang="scss">
@import "./src/assets/main.scss";

/* Modal Styling - DDA Term Modal */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.contractor-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.close-button:hover {
  background-color: #f0f0f0;
}

.modal-body {
  padding: 20px 24px;
  flex: 1;
  overflow-y: auto;
}

.search-section {
  margin-bottom: 20px;
}

.search-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.search-input {
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #4A5FD5;
}

.search-results {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-top: 10px;
}

.search-result-item {
  padding: 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.search-result-item:hover {
  background-color: #f8f9fa;
}

.search-result-item.selected {
  background-color: #e3f2fd;
  border-color: #4A5FD5;
}

.search-result-item:last-child {
  border-bottom: none;
}

.term-description {
  display: block;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.selected-term {
  margin-top: 20px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.selected-term h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  color: #333;
}

.term-details strong {
  color: #4A5FD5;
  font-size: 14px;
}

.term-details p {
  margin: 8px 0 0 0;
  color: #666;
  font-size: 13px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
  border-radius: 0 0 8px 8px;
}

.btn-secondary {
  padding: 10px 20px;
  border: 2px solid #6c757d;
  background: white;
  color: #6c757d;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: #6c757d;
  color: white;
}

.btn-primary {
  padding: 10px 20px;
  border: 2px solid #4A5FD5;
  background: #4A5FD5;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-primary:hover {
  background: #3a4fc2;
  border-color: #3a4fc2;
}

.btn-primary:disabled {
  background: #cccccc;
  border-color: #cccccc;
  cursor: not-allowed;
}

.hidden {
  display: none;
}

.company-summary-container {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1.5fr;
  gap: 1rem;
}

.tile {
  background-color: $white1;
  border: 1px solid #ccc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 5px 5px 5px 0;
  border-radius: 8px;
  box-sizing: border-box;
  padding: 20px;
  width: 100%;
}

/* Company Summary Styles */
.company-summary-header-container {
  margin-top: 30px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  
  .company-summary-header-title {
    font-size: 22px;
    font-weight: bolder;
    color: $navyBlue;
    margin-right: 20px;
  }
}

.company-summary-content {
  display: flex;
  flex-direction: row;
}

.chart-main-title {
  font-size: 18px;
  font-weight: bolder;
}

.engineering-items-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

/* Company Link Styles */
.company-items-content {
  position: relative;
  min-height: 24px;
  
  .company-title {
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
  }
}

.company-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.company-item {
  display: flex;
  align-items: center;
}

.company-link {
  color: #4A5FD5;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    text-decoration: underline;
    color: #3949AB;
  }
}

.no-companies {
  color: #999;
  font-style: italic;
}

.edit-btn {
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
  font-size: 14px;
  color: #666;
  padding: 2px 4px;
  border-radius: 3px;
  
  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
}

.company-items-content:hover .edit-btn {
  opacity: 1;
}

/* Editable GC Styles */
.editable-gc-item {
  color: $brightBlue1;
  padding: 10px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(0, 123, 255, 0.1);
    border-radius: 4px;
    padding-left: 8px;
  }
  
  .edit-icon {
    opacity: 0.6;
    font-size: 12px;
  }
}

/* Contractor Performance Styles */
.contractor-performance-header-container {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  
  .contractor-performance-header-title {
    font-size: 22px;
    font-weight: bolder;
    color: $navyBlue;
    margin-bottom: 5px;
  }
  
  .contractor-performance-subtitle {
    font-size: 14px;
    color: #666;
    margin-bottom: 20px;
  }
}

.grid-container {
  height: 400px;
  width: 100%;
  margin-top: 10px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    margin: 0;
    color: $navyBlue;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.modal-body {
  padding: 20px;
}

.search-container {
  position: relative;
}

.search-input {
  width: 100%;
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 6px;
  font-size: 16px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: $brightBlue1;
  }
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
}

.search-result-item {
  padding: 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
}

.contractor-name {
  font-weight: 600;
  color: #333;
}

.contractor-type {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  
  button {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    
    &.cancel-btn {
      background: #f8f9fa;
      color: #666;
      
      &:hover {
        background: #e9ecef;
      }
    }
    
    &.save-btn {
      background: $brightBlue1;
      color: white;
      
      &:hover:not(:disabled) {
        background: darken($brightBlue1, 10%);
      }
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

.no-data-loaded {
  padding: 10px;
}
</style>
